import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ASSETS_WITH_COUNT,
  SALE_UPDATE_ASSETS,
  SALE_ASSET_LOT_NUMBERS,
} from './queries';
import { useQuery, useMutation } from '@apollo/client';
import {
  ModalTableContainer,
  ContentContainer,
} from '../../elements';
import SubNav from './SubNav';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text } from 'react-native';
import moment from 'moment';
import styled from 'styled-components/native';

import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import { Button } from 'app/src/elements/buttons';
import Input from 'app/src/elements/inputs';
import AuthContext from 'app/src/contexts/AuthContext';

import {
  LOT_NUMBER_YEARS,
  lotSuffixesMap,
  lotSuffixOptions,
} from 'app/src/components/Sales/Process';

const LotNumberContainer = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  max-width: 100px;
  white-space: nowrap;
`;

const AddableAssets = props => {
  const { id } = useParams();
  const { lotPrefixes } = useContext(AuthContext);
  const { withNav } = props;
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    saleId: id,
    filterColumns: [],
    filterNames: [],
    scopes: ['salable']
  };

  const { data: saleAssetData } = useQuery(SALE_ASSET_LOT_NUMBERS, {
    variables: { saleId: id }
  });

  const [updateAssets] = useMutation(SALE_UPDATE_ASSETS, {
    onCompleted: () => {
      window.location.replace(`/sales/${id}`);
    }
  });

  const lotSuffixesAvailable = useMemo(
    () => lotSuffixesMap(lotPrefixes || []),
    [lotPrefixes]
  );

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  let currentYear = moment().format('YYYY');
  let years = [];
  for(let x = 0; x < 100; x++) {
    years.push(currentYear - x);
  }

  const columns = [
    {
      key: 'selected',
      label: '',
      sort: 'prevent',
      content: ({ entry }) => (
        <input
          type='checkbox'
          value={!!selected.find(asset => asset.id === entry.id)}
          onChange={e => handleChange(e.target.checked, entry)}
        />
      )
    },
    {
      key: 'lotNumber',
      label: 'Lot Number',
      sort: 'prevent',
      width: 110,
      content: ({ entry }) => {
        const item = selected.find(add => add.id === entry.id);

        if (!item) {
          return <Text></Text>;
        }

        const suffixOptions = lotSuffixOptions(
          lotSuffixesAvailable,
          item.lotNumberPrefix,
          item.lotNumberYear,
          (saleAssetData?.saleAssets || []).concat(selected),
        )

        return (
          <LotNumberContainer>
            <select
              value={item.lotNumberPrefix}
              onChange={e => (
                handleLotNumberChange(entry, { prefix: event.target.value })
              )}
            >
              <option></option>
              {lotPrefixes.map(prefix =>
                <option key={prefix.code}>{prefix.code}</option>
              )}
            </select>
            {item.lotNumberPrefix && (
              <>
                <span>-</span>
                <select
                  value={item.lotNumberYear}
                  onChange={e => (
                    handleLotNumberChange(entry, { year: event.target.value })
                  )}
                >
                  <option></option>
                  {LOT_NUMBER_YEARS.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <select
                  value={item.lotNumberSuffix}
                  onChange={e => (
                    handleLotNumberChange(entry, { suffix: event.target.value })
                  )}
                >
                  <option></option>
                  {item.lotNumberSuffix && (
                    <option
                      key="selected"
                      value={item.lotNumberSuffix}
                    >
                      {item.lotNumberSuffix}
                    </option>
                  )}
                  {suffixOptions.map(suffix => (
                    <option key={suffix} value={suffix}>{suffix}</option>
                  ))}
                </select>
              </>
            )}
          </LotNumberContainer>
        )
      }
    },
    {
      key: 'vin',
      label: 'VIN',
      content: (entry) => {
        return (
          <View>
            <Link to={`/sellers/${entry.entry.id}/assets/${entry.entry.id}`}>
              {entry.entry.vin}
            </Link>
          </View>
        )
      }
    },
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'make',
      label: 'Make',
    },
    {
      key: 'model',
      label: 'Model',
    },
    {
      key: 'seller',
      label: 'Seller',
      value: ({ value }) => value.name,
    },
    {
      key: 'clientNumber',
      label: 'Client #',
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: (entry) => {
        return (
          <View style={{display: 'inline-flex'}}>
            <Link
              to={`/sellers/${entry.entry.seller?.id}/assets/${entry.entry.id}`}
              style={{marginLeft: '10px'}}
            >
              View/Edit
            </Link>
          </View>
        )
      }
    }
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>
          <Text>Seller Name:</Text>
          <input
            type="text"
            name="seller"
            onChange={e => setFilter('seller', e.target.value)}
            value={filters.seller || ''}
          />
          <Text>VIN:</Text>
          <input
            type={'text'}
            name="vin"
            onChange={e => setFilter('vin', e.target.value)}
            value={filters.vin || ''}
          />
          <Text>Year:</Text>
          <select
            type={'select'}
            name="year"
            onChange={e => setFilter('year', e.target.value)}
            value={filters.year || ''}
          >
            <option key={`year-any`}>{'Any'}</option>)
            {years.map(year => <option key={`year-${year}`}>{year}</option>)}
          </select>
          <Text>Make:</Text>
          <input
            type={'text'}
            name="make"
            onChange={e => setFilter('make', e.target.value)}
            value={filters.make || ''}
          />
          <Text>Model:</Text>
          <input
            type={'text'}
            name="model"
            onChange={e => setFilter('model', e.target.value)}
            value={filters.model || ''}
          />
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <ModalTableContainer>
      {withNav &&
        <SubNav refetch={refetch} />
      }
      <ContentContainer>
        <Text><b>All Assets</b></Text>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={ASSETS_WITH_COUNT}
          queryName='assetsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.assetsWithCount)}
          htmlTable
        />
        <Button
          label="Add to Sale"
          onPress={() => handleSubmit()}
        />
      </ContentContainer>
    </ModalTableContainer>
  )

  function handleChange(checked, asset) {
    let newSelected = [...selected];
    let present = newSelected.find(add => add.id === asset.id);

    if (!checked && present) {
      newSelected = selected.filter(add => add.id !== asset.id);
    } else if (checked && !present) {
      newSelected.push({
        id: asset.id,
        lotNumberPrefix: '',
        lotNumberYear: '',
        lotNumberSuffix: '',
      });
    }

    setSelected(newSelected);
  }

  function handleLotNumberChange(asset, { prefix, year, suffix }) {
    const item = selected.find(add => add.id === asset.id);

    if ('undefined' !== typeof prefix) {
      item.lotNumberPrefix = prefix;
      item.lotNumberYear = null;
      item.lotNumberSuffix = null;
    }

    if ('undefined' !== typeof year) {
      item.lotNumberYear = year;
      item.lotNumberSuffix = null;
    }

    if ('undefined' !== typeof suffix) { item.lotNumberSuffix = suffix; }

    if (item.lotNumberPrefix && !item.lotNumberYear) {
      if (!asset.year) {
        item.lotNumberYear = '09';
      } else if (asset.year < 100 || asset.year > 2009) {
        item.lotNumberYear = String(asset.year % 100);
      } else {
        item.lotNumberYear = '09';
      }
    }

    if (item.lotNumberYear && !item.lotNumberSuffix) {
      const suffixOptions = lotSuffixOptions(
        lotSuffixesAvailable,
        item.lotNumberPrefix,
        item.lotNumberYear,
        (saleAssetData?.saleAssets || []).concat(selected),
      );

      item.lotNumberSuffix = suffixOptions[0];
    }

    setSelected([...selected]);
  }

  function handleSubmit() {
    const addedAssets = selected.map(asset => (
      {
        assetId: asset.id,
        lotNumberPrefix: asset.lotNumberPrefix,
        lotNumberYear: (
          asset.lotNumberYear ? parseInt(asset.lotNumberYear) : null
        ),
        lotNumberSuffix: (
          asset.lotNumberSuffix ? parseInt(asset.lotNumberSuffix) : null
        ),
      }
    ));

    updateAssets({ variables: {saleId: id, assetInputs: addedAssets} });
  }
}

export default AddableAssets;
